import {
  Button,
  Chip,
  Icon,
  Modal,
  MultipleSelect,
  Text,
  TextField,
} from 'components';
import { TEXT_STRING } from '../../constants';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  EFilterFemale,
  ESearchTab,
  TSelectOption,
  TTextFieldChangeEvent,
} from 'types';
import { useAppStore } from 'store';
import { initialFilterOptions } from 'helpers';

const str = TEXT_STRING.SEARCH_PAGE;

enum ETypeForm {
  SEARCH_INPUT = 'SEARCH_INPUT',
  MULTIPLE_SELECT = 'MULTIPLE_SELECT',
  TAGS = 'TAGS',
}

type TFields = {
  name: EFilterFemale;
  label?: string;
  type: string;
  list?: TChipList[];
  placeholder?: string;
  options?: TSelectOption[];
};

type TChipList = { id: number; label: string };

type ModalSearchProps = {
  currentTab: ESearchTab;
  onSearch: (filter: Record<string, number[] | string>) => void;
  onClose: () => void;
  open?: boolean;
  filtered?: Record<string, number[] | string>;
};

const textOptions: Record<string, string> = {
  styles: str.STYLES,
  lineage: str.LINEAGE,
  enjoy_method: str.ENJOY_METHOD,
  age_range: TEXT_STRING.CAST_PAGE.AGE_NUMBER,
  game_play: str.GAME_PLAY,
};

function ModalFilter({
  currentTab,
  open = false,
  onClose,
  onSearch,
  filtered,
}: ModalSearchProps) {
  // Hooks
  const { filterOptions, franchiseAreas } = useAppStore();

  // States
  const [listFiltered, setListFiltered] = useState<
    Record<string, number[] | string>
  >(filtered || initialFilterOptions);

  // Memo, callbacks
  const fields: TFields[] = useMemo(() => {
    const { tags } = filterOptions || {};

    return [
      {
        name: EFilterFemale.ACTIVITY_AREAS,
        type: ETypeForm.MULTIPLE_SELECT,
        label:
          currentTab === ESearchTab.SCHEDULES
            ? TEXT_STRING.CAST_PAGE.WORK_AREA
            : TEXT_STRING.CAST_PAGE.AFFILIATION_AREA,
        options: franchiseAreas
          ?.filter((area) => !area?.isOtherArea)
          ?.map((item) => ({
            name: item.name,
            value: `${item.id}`,
          })),
      },
      ...Object.keys(tags || {}).map((key) => {
        return {
          name: EFilterFemale.OPTIONS,
          type: ETypeForm.TAGS,
          label: textOptions[key] || key,
          list: tags?.[key],
        };
      }),
      {
        name: EFilterFemale.KEYWORDS,
        type: ETypeForm.SEARCH_INPUT,
        label: str.SEARCH_BY_KEYWORD,
        placeholder: str.ENTER_KEYWORD,
      },
    ];
  }, [currentTab, filterOptions, franchiseAreas]);

  const handleFilter = useCallback((value: number, key: EFilterFemale) => {
    setListFiltered((prev) => {
      const currentValues = (prev[key] as number[]) || [];
      const updatedValues = currentValues.includes(value)
        ? currentValues.filter((i) => i !== value)
        : [...currentValues, value];

      return {
        ...prev,
        [key]: updatedValues,
      };
    });
  }, []);

  const handleSearch = useCallback((e: TTextFieldChangeEvent) => {
    const value = e.target.value;
    setListFiltered((prevFilter: any) => ({
      ...prevFilter,
      keywords: value,
    }));
  }, []);

  const renderFields = useCallback(
    (fields: TFields[]) => {
      return fields.map((field, index) => {
        return (
          <div key={index}>
            <Text className="mb-2" bold textColor="text-neutral-550">
              {field.label}
            </Text>
            {(() => {
              switch (field.type) {
                case ETypeForm.SEARCH_INPUT:
                  return (
                    <>
                      <TextField
                        name="search"
                        onChange={handleSearch}
                        value={listFiltered[field.name] as string}
                        placeholder={field.placeholder}
                        prefix={<Icon icon="searchInput" />}
                        className="px-2.5 py-1 ring-inset my-2"
                      />
                      <Text textColor="text-neutral" fontWeight="font-medium">
                        {str.SEARCH_NOTE}
                      </Text>
                    </>
                  );
                case ETypeForm.MULTIPLE_SELECT:
                  const options = field?.options || [];
                  return (
                    <MultipleSelect
                      name={field.name}
                      options={options}
                      values={options?.filter((option) =>
                        (listFiltered?.[field.name] as number[])?.includes(
                          +option.value,
                        ),
                      )}
                      onChange={(values: TSelectOption[]) => {
                        setListFiltered((prev) => ({
                          ...prev,
                          [field.name]: values.map((item) => +item.value),
                        }));
                      }}
                      rounded="rounded-lg"
                    />
                  );
                case ETypeForm.TAGS:
                  return (
                    <div className="flex flex-wrap gap-3">
                      {field?.list?.map((item) => {
                        const active = (
                          listFiltered?.[field.name] as number[]
                        ).includes(item.id);
                        return (
                          <Chip
                            key={item.id}
                            ringColor={
                              active ? 'ring-primary' : 'ring-neutral-200'
                            }
                            textColor={
                              active ? 'text-primary' : 'text-neutral-300'
                            }
                            className="bg-white cursor-pointer"
                            onClick={() => handleFilter(item.id, field.name)}
                          >
                            {item.label}
                          </Chip>
                        );
                      })}
                    </div>
                  );
                default:
                  return <></>;
              }
            })()}
          </div>
        );
      });
    },
    [handleFilter, handleSearch, listFiltered],
  );

  // Effect
  useEffect(() => {
    if (filtered) {
      setListFiltered(filtered);
    }
  }, [filtered]);

  return (
    <Modal open={open} className="p-16px bg-white" onClose={() => {}}>
      <div className="flex flex-col justify-between gap-5 max-h-[65dvh] h-[70vh]">
        <div className="relative py-2">
          <div
            onClick={onClose}
            className="absolute left-0 top-1.5 cursor-pointer"
          >
            <Icon icon={'close'} />
          </div>
          <Text center bold>
            {str.REFINE}
          </Text>
          <div className="absolute right-0 top-0 cursor-pointer">
            <Button
              className="py-2"
              onClick={() => {
                setListFiltered(initialFilterOptions);
              }}
            >
              {TEXT_STRING.COMMON.RESET}
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-5 overflow-y-auto">
          {renderFields(fields)}
        </div>
        <Button
          className="w-full py-5"
          onClick={() => onSearch(listFiltered)}
          bold
        >
          {str.SEARCH}
        </Button>
      </div>
    </Modal>
  );
}

export default ModalFilter;
